import React, { useContext, useEffect, useState, FC } from "react";
import Progress from "../Progress";
import { HeaderStyled } from "./HeaderStyled";
import { Container } from "../Layout/LayoutStyled";
import { FuelContext } from "~/providers/FuelProvider";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "~/types/redux";
import { getSite } from "../../helpers/site";
import { ArrowRight } from "../Icons/fill";
import { setSingleForm } from "~/redux/actions/common";
import clientSide from "~/helpers/clientSide";
import { PAGE_ROUTES } from "~/constants/pages";
import { trackEvent } from "~/helpers/tracking";
import { CohesionContext } from "@redventures/cohesion-utils-react";
import { FrontierLogo } from "../Icons/outline";
import Cookie from "js-cookie";
import useMediaQuery from "~/hooks/useMediaQuery";
import { setStepChangeTo } from "~/redux/actions/breadcrumbsControl";
import { STEP_INDEX } from "~/helpers/breadcrumbs";
import { useChatbotContext } from "~/hooks/chatbot/useChatbotContext";
import VerizonFrontierLogo from "../Icons/outline/VerizonFrontierLogo";
import { useClientMounted } from "~/hooks/useClientMounted";
import { isMobileReductionV1 } from "~/constants/mobileProgress";
import { selectToggleMobileView } from "~/redux/selectors/offerMerchData";

const Header: FC = () => {
  const clientMounted = useClientMounted();
  const [baseUrl, setBaseUrl] = useState("/");
  const { router } = useContext(FuelContext);
  const {
    cookiePass,
    singleForm,
    pageLoading,
    quiz,
    c2f,
    breadcrumbsControl: { accordionStep },
  } = useSelector((store: ReduxState) => store);
  const { monarchPreampPlacements } = useContext(CohesionContext);
  const [showHeader, setShowHeader] = useState(false);
  const [isRedHeader, setIsRedHeader] = useState(false);
  const [showVerizonFrontierLogo, setShowVerizonFrontierLogo] = useState(false);

  const dispatch = useDispatch();
  const { isFRIN } = getSite();
  const cartFlow = Cookie.get("cartFlow") || "bauCartExperience";
  const isMobile = useMediaQuery("(max-width: 767px)");

  const isBauCartFlowInMobile = clientMounted && cartFlow === "bauCartExperience" && isMobile;
  const toggleMobileView = useSelector(selectToggleMobileView);
  const mobileReductionEnabled = isMobileReductionV1(toggleMobileView);

  const review = clientSide && router.pathname === PAGE_ROUTES.CHECKOUT && window.location.hash === "#review";
  const checkout = clientSide && router.pathname === PAGE_ROUTES.CHECKOUT && window.location.hash !== "#review";
  const unserviceableAllconnect = clientSide && router.pathname === PAGE_ROUTES.UNSERVICEABLE_ALLCONNECT;

  const hideFrontierLogo = useSelector((state: ReduxState) => state.hideFrontierLogo);
  const { chatType, showChat } = useChatbotContext();

  const handleSingleFormBack = async () => {
    if (review) {
      trackEvent({
        action: "elementClicked",
        data: {
          elementType: "Arrow Button",
          position: "back_button",
          location: "review_page",
          text: "Back",
        },
      });
      await router.push(PAGE_ROUTES.CHECKOUT);
      if (chatType !== "DISABLED") {
        showChat("back-button");
      }
    }
    if (checkout) {
      // Back Arrow on checkout for previuos step.
      if (isMobile && singleForm?.enabled) {
        if (accordionStep > STEP_INDEX.BAU.CONTACT || accordionStep > STEP_INDEX.IBC.INSTALLATION) {
          trackEvent({
            action: "elementClicked",
            data: {
              elementType: "Arrow Button",
              position: "back_button",
              location: "progress_bar_mobile",
              text: "Back to previous step",
            },
          });
          dispatch(setStepChangeTo(accordionStep - 1));
          return;
        }
      }

      dispatch(setSingleForm("contactCredit", "inactive"));
      dispatch(setSingleForm("installation", "inactive"));
      dispatch(setSingleForm("payment", "inactive"));
      dispatch(setSingleForm("dob", ""));
      dispatch(setStepChangeTo(STEP_INDEX.BAU.PLAN));
      trackEvent({
        action: "elementClicked",
        data: {
          elementType: "Arrow Button",
          position: "back_button",
          location: "checkout_page",
          text: "Back",
        },
      });
      if (quiz?.isNextGenCart) {
        const nextGenCartPage = quiz.nextGenCartPage;
        if (nextGenCartPage === "build") {
          await router.push(PAGE_ROUTES.BUILD);
        } else if (nextGenCartPage === "recommendation") {
          await router.push(PAGE_ROUTES.RECOMMENDATION);
        }
      } else {
        await router.push(PAGE_ROUTES.PLAN_PACKAGE);
      }

      if (chatType !== "DISABLED") {
        showChat("back-button");
      }
    }
  };

  useEffect(() => {
    /*
     * FRIN's homepage is /ps/
     * INFR and FRDC's homepage is /
     */

    if (isFRIN) {
      setBaseUrl("/ps/");
    }
  }, []);

  // We have an unserviceable-allconnect redesign test where the header needs to be red.
  // Once this wins/loses, we need to remove this logic.
  useEffect(() => {
    if (monarchPreampPlacements) {
      const hasAllconnectRedesignAsset = Object.values(monarchPreampPlacements).some((placement) =>
        placement.assetName.includes("allconnect-unserv-redesign")
      );

      const shouldShowRedHeader = hasAllconnectRedesignAsset && unserviceableAllconnect;
      if (isRedHeader !== shouldShowRedHeader) {
        setIsRedHeader(shouldShowRedHeader);
      }
    }
  }, [monarchPreampPlacements, unserviceableAllconnect, router.route]);

  useEffect(() => {
    if (!cookiePass) setShowHeader(true);
    // Reveal header after 2 seconds as a fallback
    setTimeout(() => setShowHeader(true), 2000);
  }, [cookiePass]);

  useEffect(() => {
    const showVerizonFrontierLogo = cartFlow === "bauCartExperience" && !isMobile;
    setShowVerizonFrontierLogo(showVerizonFrontierLogo);
  }, [cartFlow, isMobile]);

  // Note: for the Product Modularization Redesign (PMR) project, if the PMR page asset is loaded
  // there is logic in the components/PmrRedesign/index.tsx file to set display to 'none' for the default header when PMR is active.
  // we tried first putting that logic here, but the styles ended up fighting

  const showFrontierLogo =
    !hideFrontierLogo &&
    (singleForm?.enabled !== true ||
      router.route === "/" ||
      router.route.includes("plan-package") ||
      router.route.includes("liw"));
  /*
   * SSR / static export: `useClientMounted` is false (matches first client paint). Gated blocks stay
   * absent until mount, so we never emit HTML with logo/single-form chrome that the client would
   * skip on paint one. Inputs here are Redux + `router.route` (stable); `useMediaQuery` is false
   * on server and on first client render, same as pre-gate behavior for `shouldShowBreadCrumbs`.
   */
  const excludedRoutes = ["build", "recommendation", "customize/recommendation", "customize/profile"];
  const shouldShowBreadCrumbs =
    router.route !== PAGE_ROUTES.ADDRESS_CHECK && !singleForm?.enabled && !isMobile && !pageLoading;

  if (excludedRoutes.some((route) => router.route.includes(route))) return null;

  return (
    <HeaderStyled
      hidden
      showHeader={showHeader}
      isRedHeader={isRedHeader}
      isBauCartFlowInMobile={isBauCartFlowInMobile}
      mobileReductionEnabled={mobileReductionEnabled}
    >
      <Container>
        {clientMounted &&
          singleForm?.enabled === true &&
          (router.route.includes("checkout") || router.route.includes("confirmation")) && (
            <>
              {!pageLoading && !router.route.includes("confirmation") && (
                <ArrowRight onClick={handleSingleFormBack} className="checkout__back" />
              )}
              <span className="checkout__container">
                <FrontierLogo width="36" height="36" />
              </span>
            </>
          )}
        {clientMounted && showFrontierLogo && (
          <a className="frontier-logo-link" href={baseUrl} aria-label="Home">
            {showVerizonFrontierLogo ? <VerizonFrontierLogo /> : <FrontierLogo width="36" height="36" />}
          </a>
        )}
        <div id="skip-plans-portal" />
        {shouldShowBreadCrumbs && <Progress c2fEnabled={c2f?.enabled} />}
      </Container>
    </HeaderStyled>
  );
};

Header.displayName = "Header";

export default Header;
